/**
 * Generic reactive composable for loading GUI extension elements.
 * Shared logic for buttons, cards, and navigation — zero duplication.
 *
 * Each presentational component (context-buttons, context-cards, context-menu)
 * calls this with its GuiElementTypes and renders the result differently.
 */
import { type MaybeRefOrGetter, onScopeDispose, readonly, ref, toValue } from "vue";
import { getUiLanguage } from "@/shared/services/providers/language-provider.ts";
import { fetchElements } from "./context-loader.ts";
import { buildContextElements } from "./context.mapper.ts";
import type { ContextGuiElement, ElementMapper, GuiElementTypes, ViewContext } from "./context.types.ts";
import { watchImmediate } from "@vueuse/core";

export interface UseContextOptions<TElement extends ContextGuiElement> {
    context: MaybeRefOrGetter<ViewContext>;
    hasAccess: MaybeRefOrGetter<boolean>;
    mapper: ElementMapper<TElement>;
}

/**
 * Single logic source for all GUI extension element types.
 * Returns reactive `elements` list, `loading`, and `error` state.
 */
export const useGuiElements = <TElement extends ContextGuiElement>(
    type: GuiElementTypes,
    { context, hasAccess, mapper }: UseContextOptions<TElement>
) => {
    const elements = ref<TElement[]>([]);
    const loading = ref(false);
    const error = ref<Error | null>(null);

    /**
     * Race condition prevention via request tracking.
     *
     * When reactive dependencies (props, access) change rapidly, multiple async
     * loadElements() calls run in parallel. Without tracking, a slower earlier
     * response could overwrite a faster later response — showing stale data.
     *
     * How it works:
     * - Each call captures a local `requestId` snapshot from the incremented `activeRequestId`.
     * - After each async boundary (await), the local snapshot is compared to the current value.
     * - If they differ, a newer call has been started — the current one discards its result.
     * - `onScopeDispose` increments the counter to invalidate any in-flight requests
     *   when the component unmounts, preventing writes to orphaned refs and saving CPU.
     *
     * The `loading` ref is only set to `false` by the latest request to avoid
     * prematurely hiding a loading indicator while a newer request is still pending.
     */
    let activeRequestId = 0;

    const loadElements = async () => {
        if (!toValue(hasAccess)) return;

        const requestId = ++activeRequestId;
        loading.value = true;
        error.value = null;

        try {
            const elementsResult = await fetchElements(toValue(context), type);

            // Discard result if a newer request was started while waiting
            if (requestId !== activeRequestId) return;

            if (elementsResult) {
                elements.value = await buildContextElements(
                    elementsResult.uiElements,
                    elementsResult.context,
                    getUiLanguage(),
                    mapper
                );
            } else {
                elements.value = [];
            }
        } catch (e) {
            // Discard error if a newer request has superseded this one
            if (requestId !== activeRequestId) return;

            error.value = e instanceof Error ? e : new Error(String(e));
            elements.value = [];
        } finally {
            // Only the latest request may clear the loading state to prevent
            // flickering when an older response resolves before the current one
            if (requestId === activeRequestId) loading.value = false;
        }
    };

    watchImmediate([() => toValue(context), () => toValue(hasAccess), () => getUiLanguage()], loadElements, {
        deep: true,
    });

    // Invalidate any in-flight requests when the component scope is disposed
    // to prevent unnecessary work and writes to orphaned reactive refs
    onScopeDispose(() => {
        activeRequestId++;
    });

    return {
        elements: readonly(elements),
        loading: readonly(loading),
        error: readonly(error),
    };
};
